import { Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, signal, viewChild, viewChildren, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { Subscription, finalize } from 'rxjs';
import { GlobalDateFormatPipe } from '../../../../shared/pipes/global-date-format.pipe';
import { ApiService } from '../../../../core/services/api.service';
import { MicroServiceHttpService } from '../../../../core/services/microServiceHttp.service';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside.directive';
import type { Chatbot } from '../chatbot-list/chatbot-list.component';
import { AgentComponent } from './step-components/agent/agent.component';
import { WorkflowComponent } from './step-components/workflow/workflow.component';
import { KnowledgeBaseComponent } from './step-components/knowledge-base/knowledge-base.component';
import { ToolsComponent } from './step-components/tools/tools.component';
import { AnalysisComponent } from './step-components/analysis/analysis.component';
import { WhitelabelChatService } from '../../../../core/services/whitelabel-chat.service';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import { SecurityComponent } from './step-components/security/security.component';
import { AuthService } from '../../../../core/services/auth.service';
import { CommonService } from '../../../../core/services/common.service';
import { toast } from 'ngx-sonner';
import { ConfigComponent } from "./step-components/config/config.component";
import { ScheduleComponent } from "./step-components/schedule/schedule.component";

export type StepType = 'setup' | 'agent' | 'workflow' | 'knowledge-base' | 'tools' | 'analysis' | 'security' | 'config' | 'schedule';

export interface TabItem {
  id: StepType;
  label: string;
  icon: string;
  description: string;
}

@Component({
  imports: [
    CommonModule,
    FormsModule,
    IconComponentV2,
    ButtonComponent,
    GlobalDateFormatPipe,
    ClickOutsideDirective,
    AgentComponent,
    WorkflowComponent,
    KnowledgeBaseComponent,
    ToolsComponent,
    AnalysisComponent,
    SecurityComponent,
    ConfigComponent,
    ScheduleComponent,
    MarkdownModule
],
  providers: [provideMarkdown()],
  standalone: true,
  templateUrl: './chatbot-canvas.component.html',
  styleUrl: './chatbot-canvas.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    /* Markdown Content Styling - Light Theme */
    ::ng-deep .markdown-content {
      /* Headers */
      h1, h2, h3, h4, h5, h6 {
        font-weight: 600;
        margin-top: 1em;
        margin-bottom: 0.5em;
        color: #1E1B4B;
      }
      h1 { font-size: 1.5em; }
      h2 { font-size: 1.3em; }
      h3 { font-size: 1.1em; }
      h4, h5, h6 { font-size: 1em; }

      /* First header has no top margin */
      h1:first-child, h2:first-child, h3:first-child,
      h4:first-child, h5:first-child, h6:first-child {
        margin-top: 0;
      }

      /* Paragraphs */
      p {
        margin-bottom: 0.8em;
        line-height: 1.6;
      }
      p:last-child {
        margin-bottom: 0;
      }

      /* Lists */
      ul, ol {
        list-style: disc;
        margin: 0.5em 0;
        padding-left: 1.5em;
      }
      li {
        margin-bottom: 0.3em;
        line-height: 1.5;
      }
      li:last-child {
        margin-bottom: 0;
      }
      
      /* Nested lists */
      ul ul, ol ul, ul ol, ol ol {
        margin-top: 0.3em;
        margin-bottom: 0.3em;
      }

      /* Emphasis */
      strong {
        font-weight: 600;
        color: #4F46E5;
      }
      em {
        font-style: italic;
      }

      /* Code */
      code {
        background: #EDE9FE;
        padding: 0.15em 0.4em;
        border-radius: 0.25em;
        font-size: 0.9em;
        font-family: 'Courier New', monospace;
        color: #6366F1;
      }
      pre {
        background: #EDE9FE;
        padding: 0.75em;
        border-radius: 0.5em;
        overflow-x: auto;
        margin: 0.5em 0;
      }
      pre code {
        background: none;
        padding: 0;
      }

      /* Links */
      a {
        color: #6366F1;
        text-decoration: underline;
      }
      a:hover {
        color: #4F46E5;
      }

      /* Tables */
      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        margin: 1em 0;
      }
      th {
        font-weight: 700;
        color: #1E1B4B;
        text-align: left;
        padding: 0.75em 1em 0.75em 0;
        border-bottom: 2px solid #DDD6FE;
      }
      td {
        padding: 0.75em 1em 0.75em 0;
        color: #374151;
        border-bottom: 1px solid #F3F4F6;
        vertical-align: top;
      }
      tr:last-child td {
        border-bottom: none;
      }
      /* Numeric alignment hint */
      td:last-child, th:last-child {
        padding-right: 0;
      }
    }

    /* Markdown Content Styling - Dark Theme */
    :host-context(.dark):not(.disable-dark) ::ng-deep .markdown-content {
      /* Headers */
      h1, h2, h3, h4, h5, h6 {
        font-weight: 600;
        margin-top: 1em;
        margin-bottom: 0.5em;
        color: #BAE6FD;
      }
      h1 { font-size: 1.5em; }
      h2 { font-size: 1.3em; }
      h3 { font-size: 1.1em; }
      h4, h5, h6 { font-size: 1em; }

      /* First header has no top margin */
      h1:first-child, h2:first-child, h3:first-child,
      h4:first-child, h5:first-child, h6:first-child {
        margin-top: 0;
      }

      /* Paragraphs */
      p {
        margin-bottom: 0.8em;
        line-height: 1.6;
      }
      p:last-child {
        margin-bottom: 0;
      }

      /* Lists */
      ul, ol {
        list-style: disc;
        margin: 0.5em 0;
        padding-left: 1.5em;
      }
      li {
        margin-bottom: 0.3em;
        line-height: 1.5;
      }
      li:last-child {
        margin-bottom: 0;
      }
      
      /* Nested lists */
      ul ul, ol ul, ul ol, ol ol {
        margin-top: 0.3em;
        margin-bottom: 0.3em;
      }

      /* Emphasis */
      strong {
        font-weight: 600;
        color: #A78BFA;
      }
      em {
        font-style: italic;
      }

      /* Code */
      code {
        background: #5B21B6;
        padding: 0.15em 0.4em;
        border-radius: 0.25em;
        font-size: 0.9em;
        font-family: 'Courier New', monospace;
        color: #E9D5FF;
      }
      pre {
        background: #5B21B6;
        padding: 0.75em;
        border-radius: 0.5em;
        overflow-x: auto;
        margin: 0.5em 0;
      }
      pre code {
        background: none;
        padding: 0;
      }

      /* Links */
      a {
        color: #818CF8;
        text-decoration: underline;
      }
      a:hover {
        color: #A78BFA;
      }

      /* Tables */
      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        margin: 1em 0;
      }
      th {
        font-weight: 700;
        color: #BAE6FD;
        text-align: left;
        padding: 0.75em 1em 0.75em 0;
        border-bottom: 2px solid #334155;
      }
      td {
        padding: 0.75em 1em 0.75em 0;
        color: #CBD5E1;
        border-bottom: 1px solid #1E293B;
        vertical-align: top;
      }
      tr:last-child td {
        border-bottom: none;
      }
      /* Numeric alignment hint */
      td:last-child, th:last-child {
        padding-right: 0;
      }
    }
  `]
})
export class ChatbotCanvasComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiService = inject(ApiService);
  private microServiceHttp = inject(MicroServiceHttpService);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  public whitelabelChatService = inject(WhitelabelChatService);
  readonly selectedTraceId = signal<number | null>(null);
  readonly expandedToolKeys = signal<Set<string>>(new Set());

  readonly selectedTraceMessage = computed(() => {
    const id = this.selectedTraceId();
    if (id == null) return null;
    return this.whitelabelChatService.sessionMessages().find(m => m.id === id && m.role === 'assistant') || null;
  });

  /** Timeline row type used only in analysis session browser. */
  private readonly docBucketMinutes = 2;

  private safeDate(input: string | Date): Date {
    const d = input instanceof Date ? input : new Date(input);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
  }

  private bucketKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = d.getMinutes();
    const bucket = Math.floor(mm / this.docBucketMinutes) * this.docBucketMinutes;
    const mmKey = String(bucket).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mmKey}`;
  }

  readonly sessionTimeline = computed(() => {
    const messages = this.whitelabelChatService.sessionMessages() ?? [];
    const documents = this.whitelabelChatService.sessionDocuments() ?? [];

    type TimelineItem =
      | { kind: 'message'; key: string; at: Date; message: (typeof messages)[number] }
      | { kind: 'documents'; key: string; at: Date; docs: (typeof documents) };

    const items: TimelineItem[] = [];

    for (const msg of messages) {
      const at = this.safeDate(msg.createdAt);
      items.push({
        kind: 'message',
        key: `m-${msg.id}`,
        at,
        message: msg,
      });
    }

    const docBuckets = new Map<string, { at: Date; docs: (typeof documents) }>();
    for (const doc of documents) {
      const at = this.safeDate(doc.createdAt);
      const key = this.bucketKey(at);
      const bucket = docBuckets.get(key);
      if (bucket) {
        bucket.docs.push(doc);
        if (at.getTime() < bucket.at.getTime()) bucket.at = at;
      } else {
        docBuckets.set(key, { at, docs: [doc] });
      }
    }

    for (const [key, bucket] of docBuckets.entries()) {
      items.push({
        kind: 'documents',
        key: `d-${key}`,
        at: bucket.at,
        docs: bucket.docs.sort(
          (a, b) =>
            this.safeDate(a.createdAt).getTime() -
            this.safeDate(b.createdAt).getTime(),
        ),
      });
    }

    return items.sort((a, b) => a.at.getTime() - b.at.getTime());
  });

  toggleTrace(messageId: number) {
    this.selectedTraceId.update(id => id === messageId ? null : messageId);
  }

  toggleTool(key: string) {
    this.expandedToolKeys.update(set => {
      const newSet = new Set(set);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }

  private readonly toolsRef = viewChild(ToolsComponent);
  private readonly knowledgeBaseRef = viewChild(KnowledgeBaseComponent);
  private readonly tabsContainer = viewChild<ElementRef<HTMLDivElement>>('tabsContainer');
  private readonly tabButtons = viewChildren<ElementRef<HTMLButtonElement>>('tabButton');

  private document = inject(DOCUMENT);
  private stepParam = signal<string | null>(null);
  private botIdParam = signal<string | null>(null);
  private queryParamSubscription: Subscription | null = null;
  private chatbotDetailsSub: Subscription | null = null;
  private widgetScriptLoaded = false;
  /** `productName` from GET chatbot/bot/:id until products list can resolve `selectedProductId`. */
  readonly fetchedProductName = signal<string | null>(null);
  private pendingBotProductId = signal<string | null>(null);

  /** Resolved bot id: number for existing bot, null for new setup */
  readonly botId = signal<number | null>(null);

  /** True when creating a new bot (no botId or botId=new) */
  readonly isNewSetup = computed(() => {
    const id = this.botIdParam();
    return id === null || id === 'new';
  });

  readonly tabs: TabItem[] = [
    { id: 'agent', label: 'Agent', icon: 'bot', description: 'Configure agent behavior' },
    { id: 'workflow', label: 'Workflow', icon: 'git-branch', description: 'Design conversation flows' },
    { id: 'knowledge-base', label: 'Knowledge Base', icon: 'database', description: 'Manage knowledge sources' },
    { id: 'config', label: 'Widget Config', icon: 'settings', description: 'Manage widget appearance and behavior' },
    { id: 'tools', label: 'Tools', icon: 'wrench', description: 'Connect external tools' },
    { id: 'schedule', label: 'Schedules', icon: 'clock', description: 'Manage cron schedules and run history' },
    { id: 'analysis', label: 'Analysis', icon: 'chartnoaxescombined', description: 'View analytics & insights' },
    { id: 'security', label: 'Security', icon: 'shield', description: 'Security & permissions' }
  ];

  step = computed<StepType>(() => {
    const param = this.stepParam();
    const validSteps: StepType[] = ['setup', 'agent', 'workflow', 'knowledge-base', 'tools', 'analysis', 'security', 'config', 'schedule'];
    if (validSteps.includes(param as StepType)) return param as StepType;
    return this.isNewSetup() ? 'setup' : 'agent';
  });

  currentTabLabel = computed(() => {
    const value = this.step();
    const tab = this.tabs.find(t => t.id === value);
    return tab?.label ?? 'Agent';
  });

  currentTabIndex = computed(() => {
    const value = this.step();
    const index = this.tabs.findIndex(t => t.id === value);
    return index >= 0 ? index : 0;
  });

  agentName = signal<string>('');
  /** Setup form: chatbot description sent to POST chatbot/bot. */
  agentDescription = signal<string>('');
  agentCreationDate = signal<Date | null>(null);
  knowledgeBaseId = signal<number | null>(null);
  agentNameError = signal<Boolean>(false);
  /**
   * Local setup wizard panel (1 → 2). Not reflected in the URL.
   * 1: product + agent name; 2: optional description, then create.
   */
  readonly setupWizardStep = signal<1 | 2>(1);
  /** `id` is product `pid` (string) for setup/API; `apiId` is list-product `id` for app-wide selected product. */
  productsList = signal<Array<{ id: string; name: string; keyname?: string; apiId: string }>>([]);
  selectedProductId = signal<string | null>(null);
  productError = signal<boolean>(false);
  isProductDropdownOpen = signal<boolean>(false);
  /** Viewport-fixed panel position (dropdown rendered outside overflow/transform chain). */
  readonly productDropdownTop = signal(0);
  readonly productDropdownLeft = signal(0);
  readonly productDropdownWidth = signal(0);
  private readonly productTriggerBtn = viewChild<ElementRef<HTMLButtonElement>>('productTriggerBtn');
  private readonly boundSyncProductDropdown = (): void => {
    if (this.isProductDropdownOpen()) {
      this.updateProductDropdownPosition();
    }
  };
  /** True while POST chatbot/bot is in flight. */
  isCreatingAgent = signal(false);
  selectedProductName = computed(() => {
    const id = this.selectedProductId();
    if (!id) return null;
    const product = this.productsList().find((p) => p.id === id);
    return product ? product.name : null;
  });

  /**
   * If a product is already selected globally (sidebar), lock the setup wizard's
   * product selection so the user cannot change it here.
   */
  readonly isSetupProductLocked = computed(() => {
    const globalApiId =
      this.apiService.getSelectedProductId() ||
      sessionStorage.getItem('selected_product_id');
    if (!globalApiId) return false;
    const match = this.productsList().find(
      (p: { apiId: string }) => p.apiId === String(globalApiId),
    );
    return !!match;
  });

  /** Header line: prefer product dropdown (setup), then API `productName`. */
  readonly headerProductLabel = computed(() => {
    return this.selectedProductName() ?? this.fetchedProductName();
  });

  toggleProductDropdown() {
    if (this.isSetupProductLocked()) return;
    const next = !this.isProductDropdownOpen();
    this.isProductDropdownOpen.set(next);
    if (next) {
      queueMicrotask(() => this.updateProductDropdownPosition());
    }
  }

  closeProductDropdown() {
    this.isProductDropdownOpen.set(false);
  }

  private updateProductDropdownPosition(): void {
    const btn = this.productTriggerBtn()?.nativeElement;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const gap = 8;
    this.productDropdownTop.set(r.bottom + gap);
    this.productDropdownLeft.set(r.left);
    this.productDropdownWidth.set(r.width);
  }

  // --- Tab Scrolling logic ---
  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);

  updateScrollState() {
    const el = this.tabsContainer()?.nativeElement;
    if (!el) return;
    
    // Check if we can scroll left (scrollLeft > 0)
    // We use a slightly larger threshold (10px) to handle padding/subpixel issues
    const currentScroll = Math.ceil(el.scrollLeft);
    this.canScrollLeft.set(currentScroll > 10);
    
    // Check if we can scroll right (scrollLeft + clientWidth < scrollWidth)
    this.canScrollRight.set(currentScroll + el.clientWidth < el.scrollWidth - 10);
  }

  scrollTabs(direction: 'left' | 'right') {
    const el = this.tabsContainer()?.nativeElement;
    if (!el) return;
    const scrollAmount = 240;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  }

  goToChatbotList() {
    this.router.navigate(['../list'], { relativeTo: this.route });
  }

  /** Setup wizard: same destination as list back. */
  goBack() {
    this.goToChatbotList();
  }

  private parseChatbotResponse(response: unknown): Chatbot | null {
    const r = response as { data?: Chatbot } | Chatbot | null | undefined;
    const raw = r && typeof r === 'object' && 'data' in r ? (r as { data?: Chatbot }).data : (r as Chatbot);
    if (!raw || typeof raw !== 'object') return null;
    return raw;
  }

  private applyChatbotFromApi(bot: Chatbot): void {
    this.agentName.set(bot.name ?? '');
    this.agentDescription.set(bot.description ?? '');
    this.fetchedProductName.set(bot.productName ?? null);
    const created = new Date(bot.createdAt);
    this.agentCreationDate.set(Number.isNaN(created.getTime()) ? null : created);
    this.knowledgeBaseId.set(bot.knowledgeBaseId ?? null);
    this.pendingBotProductId.set(bot.productId != null ? String(bot.productId) : null);
    this.syncPendingProductSelection();
  }

  /** Binds `selectedProductId` once `loadProducts` has run (order vs. chatbot GET may vary). */
  private syncPendingProductSelection(): void {
    const pending = this.pendingBotProductId();
    if (!pending) return;
    const products = this.productsList();
    if (products.length === 0) return;
    const match = products.find((p) => p.id === pending || p.apiId === pending);
    if (match) {
      this.selectedProductId.set(match.id);
      // Keep global product context aligned (sidebar uses products.id / apiId).
      this.apiService.setSelectedProductId(match.apiId ?? null);
    }
    this.pendingBotProductId.set(null);
  }

  private loadChatbotDetails(botId: number): void {
    this.chatbotDetailsSub?.unsubscribe();
    this.chatbotDetailsSub = this.microServiceHttp
      .get<unknown>('whitelabel', `chatbot/bot/${botId}`, {
        showToast: false,
        showLoader: true,
      })
      .subscribe({
        next: (response) => {
          if (this.botId() !== botId) return;
          const bot = this.parseChatbotResponse(response);
          if (bot) {
            this.applyChatbotFromApi(bot);
          }
        },
        error: () => {
          if (this.botId() !== botId) return;
          this.agentName.set('');
          this.agentDescription.set('');
          this.fetchedProductName.set(null);
          this.agentCreationDate.set(null);
          this.knowledgeBaseId.set(null);
          this.pendingBotProductId.set(null);
        },
      });
  }

  ngOnInit() {
    this.queryParamSubscription = this.route.queryParamMap.subscribe((params) => {
      const step = params.get('step');
      const botIdRaw = params.get('botId');
      this.stepParam.set(step);
      this.botIdParam.set(botIdRaw);

      const numId = botIdRaw !== null && botIdRaw !== 'new' ? parseInt(botIdRaw, 10) : NaN;
      const resolvedId = Number.isNaN(numId) ? null : numId;
      this.botId.set(resolvedId);

      if (resolvedId !== null) {
        this.loadChatbotDetails(resolvedId);
        this.createOrUpdateWidget(resolvedId);
      } else {
        this.chatbotDetailsSub?.unsubscribe();
        this.chatbotDetailsSub = null;
        this.fetchedProductName.set(null);
        this.pendingBotProductId.set(null);
        this.setupWizardStep.set(1);
        this.removeWidgetElement();
      }

      // Log botId on load / when params change (for later API use)
      console.log('ChatbotCanvas botId', resolvedId, resolvedId === null ? '(new setup)' : `(existing bot ${resolvedId})`);

      // Existing bot but step is setup or missing -> go to agent step
      if (resolvedId !== null && (step === 'setup' || !step)) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { botId: resolvedId, step: 'agent' },
          queryParamsHandling: 'merge',
        });
      }
      // New setup but step missing -> ensure botId=new and step=setup in URL
      if (resolvedId === null && !step) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { botId: 'new', step: 'setup' },
          queryParamsHandling: 'merge',
        });
      }
    });

    this.loadProducts();
    this.loadWidgetScript();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.boundSyncProductDropdown);
      window.addEventListener('scroll', this.boundSyncProductDropdown, true);
    }
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.boundSyncProductDropdown);
      window.removeEventListener('scroll', this.boundSyncProductDropdown, true);
    }
    this.stepParam.set(null);
    this.botIdParam.set(null);
    this.botId.set(null);
    this.queryParamSubscription?.unsubscribe();
    this.chatbotDetailsSub?.unsubscribe();
    this.removeWidgetElement();
    // Cleanup panel count if destroyed while open
    if (this.toolDrawerOpen()) {
      this.commonService.updateBodyPanelCount(-1);
    }
  }

  constructor() {
    effect(() => {
      const open = this.toolDrawerOpen() || this.whitelabelChatService.isAnalysisDrawerOpen();
      this.commonService.updateBodyPanelCount(open ? 1 : -1);
    });

    // Auto-scroll active tab into view
    effect(() => {
      const index = this.currentTabIndex();
      const buttons = this.tabButtons();
      const el = this.tabsContainer()?.nativeElement;
      const activeBtn = buttons[index]?.nativeElement;
      
      // Only scroll into view if there is actual overflow to handle
      if (activeBtn && el && el.scrollWidth > el.clientWidth) {
        activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });

    // Initial scroll state and resize listener
    effect(() => {
      const el = this.tabsContainer()?.nativeElement;
      if (el) {
        // Initial check
        setTimeout(() => this.updateScrollState(), 100);

        const resizeObserver = new ResizeObserver(() => this.updateScrollState());
        resizeObserver.observe(el);
        return () => resizeObserver.disconnect();
      }
      return;
    });
  }

  private loadWidgetScript(): Promise<void> {
    return new Promise((resolve) => {
      if (this.widgetScriptLoaded) {
        resolve();
        return;
      }
      
      const existingScript = this.document.querySelector('script[src*="prism-chatbot-widget.js"]');
      if (existingScript) {
        this.widgetScriptLoaded = true;
        resolve();
        return;
      }

      const script = this.document.createElement('script');
      script.src = '/prism-chatbot-widget.js';
      script.async = true;
      script.id = 'prism-chatbot-widget-script';
      script.onload = () => {
        this.widgetScriptLoaded = true;
        resolve();
      };
      this.document.body.appendChild(script);
    });
  }

  private async createOrUpdateWidget(botId: number): Promise<void> {
    // Ensure widget script is loaded first
    await this.loadWidgetScript();
    
    // Remove existing widget if any
    this.removeWidgetElement();
    
    // Create host div to bypass widget's internal shadow DOM styles (!important)
    const host = this.document.createElement('div');
    host.id = 'prism-chatbot-host';
    host.style.position = 'fixed';
    host.style.bottom = '0';
    host.style.right = '0';
    host.style.zIndex = '10000'; // Match widget's default z-index

    // Create new widget element
    const widget = this.document.createElement('prism-chatbot');
    widget.setAttribute('agent-id', String(botId));
    widget.setAttribute('source', 'prism');
    widget.setAttribute('data-canvas', 'true');
    
    // Optionally set user-id for session management
    const userId = this.authService.getUserPid();
    if (userId) {
      widget.setAttribute('user-id', String(userId));
    }
    
    // Append widget to host, and host to body
    host.appendChild(widget);
    this.document.body.appendChild(host);
  }

  private removeWidgetElement(): void {
    const host = this.document.getElementById('prism-chatbot-host');
    if (host) {
      host.remove();
    }
    // Backup: check for loose widgets
    const widget = this.document.querySelector('prism-chatbot[data-canvas="true"]');
    if (widget) {
      widget.remove();
    }
  }

  setStep(tab: StepType) {
    if (tab === 'setup') return;
    const botId = this.botIdParam();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: tab, ...(botId != null && { botId }) },
      queryParamsHandling: 'merge',
    });
  }

  isActiveTab(tabId: StepType): boolean {
    return this.step() === tabId;
  }

  isTabCompleted(tabId: StepType): boolean {
    const currentIndex = this.currentTabIndex();
    const tabIndex = this.tabs.findIndex(t => t.id === tabId);
    return tabIndex < currentIndex;
  }

  navigateToNextStep() {
    const currentIndex = this.currentTabIndex();
    if (currentIndex < this.tabs.length - 1) {
      this.setStep(this.tabs[currentIndex + 1].id);
    }
  }

  navigateToPreviousStep() {
    const currentIndex = this.currentTabIndex();
    if (currentIndex > 0) {
      this.setStep(this.tabs[currentIndex - 1].id);
    }
  }

  getTabClasses(tab: TabItem): string {
    const isActive = this.isActiveTab(tab.id);
    const baseClasses = 'group relative flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-all duration-300 ease-out cursor-pointer select-none';

    if (isActive) {
      return `${baseClasses} text-purple-600 dark:text-purple-400`;
    }

    return `${baseClasses} text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200`;
  }

  onAgentNameInput(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value ?? '';
    this.agentName.set(value.slice(0, 50));
  }

  private readonly maxAgentDescriptionLength = 1000;

  onAgentDescriptionInput(event: Event) {
    const target = event.target as HTMLTextAreaElement | null;
    const value = target?.value ?? '';
    this.agentDescription.set(value.slice(0, this.maxAgentDescriptionLength));
  }

  /** Step 1 → 2: requires product and agent name only. */
  onSetupWizardNext(): void {
    const name = this.agentName().trim();
    const hasAgentName = !!name;
    const hasProduct = !!this.selectedProductId();

    if (!hasAgentName || !hasProduct) {
      this.agentNameError.set(!hasAgentName);
      this.productError.set(!hasProduct);

      setTimeout(() => {
        this.agentNameError.set(false);
        this.productError.set(false);
      }, 3000);

      return;
    }

    this.agentNameError.set(false);
    this.productError.set(false);
    this.closeProductDropdown();
    this.setupWizardStep.set(2);
  }

  /** Step 2 → 1, or step 1 → chatbot list. */
  onSetupWizardBack(): void {
    if (this.setupWizardStep() === 2) {
      this.setupWizardStep.set(1);
    } else {
      this.goBack();
    }
  }

  createAgent() {
    const name = this.agentName().trim();
    const description = this.agentDescription().trim();
    const hasAgentName = !!name;
    const hasProduct = !!this.selectedProductId();

    if (!hasAgentName || !hasProduct) {
      this.agentNameError.set(!hasAgentName);
      this.productError.set(!hasProduct);

      setTimeout(() => {
        this.agentNameError.set(false);
        this.productError.set(false);
      }, 3000);

      return;
    }

    const productIdStr = this.selectedProductId();
    const productIdNum = productIdStr ? parseInt(productIdStr, 10) : NaN;
    const userPid = this.authService.getUserPid();

    if (userPid == null) {
      toast.error('Invalid user or product id, please try again later.');
      this.goToChatbotList();
      return;
    }
    if (Number.isNaN(productIdNum)) {
      this.productError.set(true);
      return;
    }

    this.agentNameError.set(false);
    this.productError.set(false);

    if (productIdStr) {
      const p = this.productsList().find((x) => x.id === productIdStr);
      this.apiService.setSelectedProductId(p?.apiId ?? productIdStr);
    }

    this.isCreatingAgent.set(true);
    this.microServiceHttp
      .post<unknown>(
        'whitelabel',
        'chatbot/bot',
        {
          name,
          description,
          productId: productIdNum,
          userId: userPid,
        },
        {
          showLoader: true,
          showToast: true,
          successMessage: 'Chatbot created successfully',
        },
      )
      .pipe(finalize(() => this.isCreatingAgent.set(false)))
      .subscribe({
        next: (response) => {
          const bot = this.parseChatbotResponse(response);
          const pid = bot?.pid;
          if (bot && pid != null && !Number.isNaN(Number(pid))) {
            this.agentName.set(bot.name ?? name);
            this.agentDescription.set(bot.description ?? description);
            const created = new Date(bot.createdAt);
            this.agentCreationDate.set(Number.isNaN(created.getTime()) ? new Date() : created);
            this.fetchedProductName.set(bot.productName ?? null);
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { botId: pid, step: 'agent' },
              queryParamsHandling: 'merge',
            });
            return;
          }
          this.goToChatbotList();
        },
        error: () => {
          this.goToChatbotList();
        },
      });
  }

  onProductChange(productId: string | null) {
    if (this.isSetupProductLocked()) return;
    this.selectedProductId.set(productId);
    this.productError.set(!productId);
    this.closeProductDropdown();
  }

  private loadProducts() {
    if (this.productsList().length > 0) return;

    this.apiService.fetchProducts(true).subscribe({
      next: (response: any) => {
        const stored = sessionStorage.getItem('dynamic_routes');
        const dynamicRoutes = stored ? JSON.parse(stored) : [];

        const allowedProductIds = dynamicRoutes
          .map((item: any) => item.product?.id)
          .filter((id: any) => !!id);

        const filteredProducts = (response || [])
          .filter(
            (item: any) =>
              item.active &&
              allowedProductIds.includes(item.id) &&
              item.pid != null &&
              !Number.isNaN(Number(item.pid))
          )
          .map((item: any) => ({
            id: String(item.pid),
            apiId: String(item.id),
            name: item.name,
            keyname: item.keyname,
          }));

        this.productsList.set(filteredProducts);

        // Default selection from global sidebar product (apiId → pid string for setup).
        if (!this.selectedProductId()) {
          const globalApiId =
            this.apiService.getSelectedProductId() ||
            sessionStorage.getItem('selected_product_id');
          if (globalApiId) {
            const globalMatch = filteredProducts.find(
              (p: { apiId: string }) => p.apiId === String(globalApiId),
            );
            if (globalMatch) {
              this.selectedProductId.set(globalMatch.id);
            }
          }
        }

        this.syncPendingProductSelection();
      },
      error: () => {
        this.productsList.set([]);
        this.selectedProductId.set(null);
        this.pendingBotProductId.set(null);
      },
    });
  }

  // ── Tools drawer (lives at canvas level to escape backdrop-blur stacking context) ──────────

  readonly toolDrawerOpen = signal(false);
  readonly toolSaving = signal(false);
  readonly editingToolId = signal<number | null>(null);
  private _toolBotId = 0;

  readonly toolForm = signal({
    name: '', description: '', guardrails: '', isGlobal: false,
    toolType: 'DATABASE' as 'DATABASE' | 'API',
    mysqlQuery: '',
    apiUrl: '', apiMethod: 'GET' as 'GET'|'POST'|'PUT'|'PATCH'|'DELETE',
    apiHeaders: '', apiBody: '', apiParams: '', apiQuery: '',
    apiAuthToken: '', apiAuthType: '' as 'HEADER'|'BEARER'|'BODY'|'QUERY'|'', apiAuthKeyname: '',
  });

  readonly toolFormErrors = signal<Record<string, string>>({});
  /** The tool being edited already stores a secret; prism-new reports only `hasAuthToken`, never the value. */
  private editingToolHasToken = false;
  readonly isToolDatabase = computed(() => this.toolForm().toolType === 'DATABASE');

  readonly toolApiMethodOptions = ['GET', 'POST'] as const;
  readonly toolAuthTypeOptions = [
    { value: 'HEADER' as const, label: 'Header key' },
    { value: 'BEARER' as const, label: 'Bearer' },
    { value: 'BODY'  as const, label: 'Body' },
    { value: 'QUERY'  as const, label: 'Query' },
  ];

  /** Placeholder strings — kept in class to avoid Angular {{}} template binding errors. */
  readonly toolPh = {
    varSyntax: '{{variable}}',
    dbQuery:   'SELECT * FROM contracts WHERE contract_id = {{contract_id}}',
    apiUrl:    'https://api.example.com/{{resource}}',
    apiParams: '{"id": "{{id}}"}',
    apiQuery:  '{"q": "{{search}}"}',
  };

  openToolsDrawer(): void {
    const id = this.botId();
    if (id == null) return;
    this._toolBotId = id;
    this.editingToolId.set(null);
    this.toolForm.set({
      name: '', description: '', guardrails: '', isGlobal: false,
      toolType: 'DATABASE',
      mysqlQuery: '',
      apiUrl: '', apiMethod: 'GET',
      apiHeaders: '', apiBody: '', apiParams: '', apiQuery: '',
      apiAuthToken: '', apiAuthType: '', apiAuthKeyname: '',
    });
    this.editingToolHasToken = false;
    this.toolFormErrors.set({});
    this.toolDrawerOpen.set(true);
  }

  openEditToolsDrawer(tool: any): void {
    const id = this.botId();
    if (id == null) return;
    this._toolBotId = id;
    this.editingToolId.set(tool.pid);
    this.editingToolHasToken = !!(tool.apiAuthToken || tool.hasAuthToken);
    
    const stringifyBody = (data: any) => {
      if (!data || Object.keys(data).length === 0) return '';
      return JSON.stringify(data, null, 2);
    };

    this.toolForm.set({
      name: tool.name || '',
      description: tool.description || '',
      guardrails: tool.guardrails || '',
      isGlobal: !!tool.isGlobal,
      toolType: tool.toolType as 'DATABASE' | 'API',
      mysqlQuery: tool.mysqlQuery || '',
      apiUrl: tool.apiUrl || '',
      apiMethod: (tool.apiMethod as any) || 'GET',
      apiHeaders: stringifyBody(tool.apiHeaders),
      apiBody: stringifyBody(tool.apiBody),
      apiParams: stringifyBody(tool.apiParams),
      apiQuery: stringifyBody(tool.apiQuery),
      apiAuthToken: tool.apiAuthToken || '',
      apiAuthType: (tool.apiAuthType as any) || '',
      apiAuthKeyname: tool.apiAuthKeyname || 'Authorization',
    });
    this.toolFormErrors.set({});
    this.toolDrawerOpen.set(true);
  }

  closeToolsDrawer(): void {
    if (this.toolSaving()) return;
    this.toolDrawerOpen.set(false);
  }

  patchToolForm(patch: Partial<ReturnType<typeof this.toolForm>>): void {
    this.toolForm.update(f => ({ ...f, ...patch }));
    const keys = Object.keys(patch);
    this.toolFormErrors.update(e => {
      const next = { ...e };
      keys.forEach(k => delete next[k]);
      return next;
    });
  }

  setToolType(t: 'DATABASE' | 'API'): void {
    this.toolForm.update(f => ({ ...f, toolType: t }));
    this.toolFormErrors.set({});
  }

  private tryParseJson(raw: string): Record<string, unknown> | null {
    const s = raw.trim();
    if (!s) return null;
    try {
      const p = JSON.parse(s);
      return p && typeof p === 'object' && !Array.isArray(p) ? p : null;
    } catch { return null; }
  }

  private validateToolForm(): boolean {
    const f = this.toolForm();
    const errors: Record<string, string> = {};
    if (!f.name.trim()) errors['name'] = 'Name is required.';
    if (!f.description.trim()) errors['description'] = 'Description is required.';
    if (f.toolType === 'DATABASE') {
      if (!f.mysqlQuery.trim()) errors['mysqlQuery'] = 'MySQL query is required.';
    } else {
      if (!f.apiUrl.trim()) errors['apiUrl'] = 'API URL is required.';
      const jsonFields = [
        { key: 'apiHeaders', label: 'Headers' },
        { key: 'apiBody',    label: 'Body' },
        { key: 'apiParams',  label: 'Params' },
        { key: 'apiQuery',   label: 'Query' },
      ] as const;
      for (const { key, label } of jsonFields) {
        const val = (f[key] as string).trim();
        if (val && this.tryParseJson(val) === null)
          errors[key] = `${label} must be valid JSON.`;
      }
      
      if (f.apiAuthType) {
        // Left blank on an edit, the stored secret is kept (the body omits the key).
        if (!f.apiAuthToken.trim() && !this.editingToolHasToken) errors['apiAuthToken'] = 'Token / Secret is required.';
        if (!f.apiAuthKeyname.trim()) errors['apiAuthKeyname'] = 'Key name is required.';
      }
    }
    this.toolFormErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  submitTool(): void {
    if (!this.validateToolForm()) return;
    const f = this.toolForm();
    const chatbotId = this._toolBotId;
    if (!chatbotId) return;

    const body: Record<string, unknown> = {
      toolType: f.toolType,
      name: f.name.trim(),
      description: f.description.trim(),
      guardrails: f.guardrails.trim() || undefined,
      isGlobal: f.isGlobal,
    };

    body['chatbotId'] = !this.editingToolId() ? chatbotId : undefined;

    if (f.toolType === 'DATABASE') {
      body['mysqlQuery'] = f.mysqlQuery.trim();
    } else {
      body['apiUrl'] = f.apiUrl.trim();
      body['apiMethod'] = f.apiMethod;
      const h = this.tryParseJson(f.apiHeaders);
      const b = this.tryParseJson(f.apiBody);
      const p = this.tryParseJson(f.apiParams);
      const q = this.tryParseJson(f.apiQuery);
      if (h) body['apiHeaders'] = h;
      if (b) body['apiBody'] = b;
      if (p) body['apiParams'] = p;
      if (q) body['apiQuery'] = q;
      if (f.apiAuthType) {
        body['apiAuthType'] = f.apiAuthType;
        body['apiAuthToken'] = f.apiAuthToken.trim() || undefined;
        body['apiAuthKeyname'] = f.apiAuthKeyname.trim() || 'Authorization';
      }
    }

    this.toolSaving.set(true);
    
    const editingId = this.editingToolId();
    const req$ = editingId
      ? this.microServiceHttp.patch<unknown>('whitelabel', `tools/${editingId}`, body, {
          showToast: true,
          successMessage: 'Tool updated successfully',
          showLoader: false,
        })
      : this.microServiceHttp.post<unknown>('whitelabel', 'tools', body, {
          showToast: true,
          successMessage: 'Tool created successfully',
          showLoader: false,
        });

    req$
      .pipe(finalize(() => this.toolSaving.set(false)))
      .subscribe({
        next: () => {
          this.toolDrawerOpen.set(false);
          // Refresh the tools list inside the child component
          this.toolsRef()?.loadTools(chatbotId);
        },
      });
  }

  handleKnowledgeBaseUpload(event: { collectionId: number }): void {
    const bid = this.botId();
    if (bid == null) return;

    // Link only if the chatbot doesn't already have a knowledgeBaseId
    if (this.knowledgeBaseId() == null) {
      const payload = {
        chatbotId: bid,
        collectionId: event.collectionId
      };

      this.microServiceHttp.post('whitelabel', 'knowledge-base/collection/link', payload, {
        showLoader: false,
        showToast: false
      }).subscribe({
        next: () => {
          this.knowledgeBaseId.set(event.collectionId);
          this.knowledgeBaseRef()?.loadDocuments(bid, null);
        }
      });
    } else {
      // Already linked globally, just refresh the listing
      this.knowledgeBaseRef()?.loadDocuments(bid, null);
    }
  }
}
